import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";

import Alert from "veneer/Alert";
import Drawer from "veneer/Drawer";
import { showError, showSuccess } from "veneer/SnackBar";
import Stack from "veneer/Stack";
import { Colors } from "veneer/Theme";
import Tooltip from "veneer/Tooltip";
import Typography from "veneer/Typography";

import { Device } from "cortex/ui/src/fleet/view/device/model";
import { useAppDispatch } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import { BULK_RESTART_DEVICES } from "cortex/ui/src/redux-store/devices/actions";
import { DeviceStatusIcon } from "cortex/ui/src/ui/table/component/DeviceStatusIcon";

import BaseDrawer, { ListItem, ListWrapper } from "./BaseDrawer";

interface RestartDeviceDrawerProps {
  onCancel: () => void;
  open: boolean;
  selectedItems: ReadonlyArray<Device>;
  setSelectedItems: (items: ReadonlyArray<Device>) => void;
}

interface UpgradeListItemProps {
  device: Device;
}

export default function RestartDeviceDrawer({
  onCancel,
  open,
  selectedItems,
  setSelectedItems,
}: RestartDeviceDrawerProps) {
  const dispatch = useAppDispatch();
  const [submitted, setSubmitted] = useState(false);

  const handleRestartDevices = useCallback(async () => {
    if (selectedItems.length === 0) {
      return;
    }
    const selectedDeviceIds = selectedItems.map((device) => device.id);
    setSubmitted(true);

    try {
      await createPromiseApiAction(dispatch, BULK_RESTART_DEVICES, [
        selectedDeviceIds,
      ]);
      showSuccess(
        `Device restart request processed successfully. Your devices will restart shortly.`,
      );
    } catch (e) {
      showError(
        "An error occurred while attempting to restart the selected devices.",
      );
    } finally {
      setSelectedItems([]);
      setSubmitted(false);
      onCancel();
    }
  }, [dispatch, onCancel, selectedItems, setSelectedItems]);

  const drawerHeader = (
    <Alert
      dense
      severity="warning"
      title={`You are about to restart ${selectedItems.length} device(s).`}
    >
      For any WebOS devices, the entire operating system will reboot.
      <br />
      <b>Note:</b> This operation will take a few minutes to complete.
    </Alert>
  );

  return (
    <Drawer
      commitLabel="Restart"
      commitLoading={submitted}
      onCancel={onCancel}
      onClose={onCancel}
      onCommit={handleRestartDevices}
      open={open}
      padding={0}
      title="Restart Devices"
    >
      <BaseDrawer drawerHeader={drawerHeader}>
        {selectedItems.map((device) => (
          <ListWrapper disablePadding key={device.id}>
            <UpgradeListItem device={device} />
          </ListWrapper>
        ))}
      </BaseDrawer>
    </Drawer>
  );
}

const UpgradeListItem = ({ device }: UpgradeListItemProps) => {
  return (
    <ListItem>
      <Stack alignItems="center" direction="row" spacing={2}>
        <DeviceStatusIcon device={device} />
        <Stack flex={1}>
          <Tooltip title={device.device_id}>
            <Link to={`/devices/${device.device_id}`}>
              <Typography color={Colors.gray1000} variant="body1">
                {device.name}
              </Typography>
            </Link>
          </Tooltip>
          <Typography variant="body2">
            <Link to={`/networks/${device.network_id}`}>
              {device.network_name}
            </Link>
          </Typography>
        </Stack>
      </Stack>
    </ListItem>
  );
};
